import React, { useState, useEffect } from "react";
import "./AttendanceTracker.css";
import {
  FaCalendarCheck,
  FaArrowLeft,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSave,
  FaFilter,
  FaUsers,
} from "react-icons/fa";

const AttendanceTracker = ({ token, onBack }) => {
  const [selectedClass, setSelectedClass] = useState("Nursery A");
  const [attendanceDate, setAttendanceDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [studentList, setStudentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // { type: 'success' | 'error', text: '' }

  // Simulated fetch students for selected class
  useEffect(() => {
    const fetchClassStudents = async () => {
      setLoading(true);
      setSaveStatus(null);
      try {
        // Replace with API endpoint: apiService.getStudentsByClass(selectedClass, attendanceDate, token)
        setTimeout(() => {
          const mockClassData = [
            { id: 101, name: "Aarav Sharma", rollNo: "01", status: "Present" },
            { id: 102, name: "Ananya Patel", rollNo: "02", status: "Present" },
            { id: 103, name: "Kabir Verma", rollNo: "03", status: "Absent" },
            { id: 104, name: "Diya Iyer", rollNo: "04", status: "Late" },
            { id: 105, name: "Vivaan Gupta", rollNo: "05", status: "Present" },
            { id: 106, name: "Riya Sen", rollNo: "06", status: "Present" },
          ];
          setStudentList(mockClassData);
          setLoading(false);
        }, 400);
      } catch (error) {
        console.error("Failed to load class attendance:", error);
        setLoading(false);
      }
    };

    fetchClassStudents();
  }, [selectedClass, attendanceDate, token]);

  // Handler to change individual student status
  const handleStatusChange = (id, newStatus) => {
    setStudentList((prev) =>
      prev.map((student) =>
        student.id === id ? { ...student, status: newStatus } : student
      )
    );
  };

  // Quick action: Mark all present
  const handleMarkAll = (status) => {
    setStudentList((prev) =>
      prev.map((student) => ({ ...student, status }))
    );
  };

  // Save attendance batch
  const handleSaveAttendance = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    try {
      // Replace with API call: apiService.saveAttendanceBatch({ selectedClass, attendanceDate, studentList }, token)
      await new Promise((resolve) => setTimeout(resolve, 800));

      setSaveStatus({
        type: "success",
        text: `Attendance for ${selectedClass} on ${attendanceDate} saved successfully!`,
      });
    } catch (error) {
      console.error("Save error:", error);
      setSaveStatus({
        type: "error",
        text: "Failed to submit attendance. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate quick stats
  const presentCount = studentList.filter((s) => s.status === "Present").length;
  const absentCount = studentList.filter((s) => s.status === "Absent").length;
  const lateCount = studentList.filter((s) => s.status === "Late").length;

  return (
    <div className="attendance-tracker-container">
      {/* Header Bar */}
      <div className="attendance-header">
        <div className="header-left">
          <button className="back-btn" onClick={onBack} type="button">
            <FaArrowLeft /> Back to Dashboard
          </button>
          <h2>
            <FaCalendarCheck className="header-icon" /> Daily Attendance Tracker
          </h2>
        </div>
      </div>

      {/* Control Panel: Class & Date Selection */}
      <div className="attendance-controls">
        <div className="control-group">
          <label htmlFor="classSelect">
            <FaFilter className="icon" /> Select Class / Section:
          </label>
          <select
            id="classSelect"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <option value="Playgroup A">Playgroup A</option>
            <option value="Playgroup B">Playgroup B</option>
            <option value="Nursery A">Nursery A</option>
            <option value="Nursery B">Nursery B</option>
            <option value="LKG A">LKG A</option>
            <option value="UKG A">UKG A</option>
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="attendanceDate">
            <FaCalendarCheck className="icon" /> Attendance Date:
          </label>
          <input
            type="date"
            id="attendanceDate"
            value={attendanceDate}
            onChange={(e) => setAttendanceDate(e.target.value)}
          />
        </div>
      </div>

      {/* Summary Counters & Bulk Actions */}
      <div className="attendance-summary-bar">
        <div className="summary-badges">
          <span className="badge total">
            <FaUsers /> Total: {studentList.length}
          </span>
          <span className="badge present">
            <FaCheckCircle /> Present: {presentCount}
          </span>
          <span className="badge absent">
            <FaTimesCircle /> Absent: {absentCount}
          </span>
          <span className="badge late">
            <FaClock /> Late: {lateCount}
          </span>
        </div>

        <div className="bulk-actions">
          <button
            type="button"
            className="btn-mark-all present"
            onClick={() => handleMarkAll("Present")}
          >
            Mark All Present
          </button>
          <button
            type="button"
            className="btn-mark-all absent"
            onClick={() => handleMarkAll("Absent")}
          >
            Mark All Absent
          </button>
        </div>
      </div>

      {/* Notification Banner */}
      {saveStatus && (
        <div className={`status-banner ${saveStatus.type}`}>
          {saveStatus.type === "success" ? (
            <FaCheckCircle className="banner-icon" />
          ) : (
            <FaTimesCircle className="banner-icon" />
          )}
          <span>{saveStatus.text}</span>
        </div>
      )}

      {/* Attendance Sheet Table */}
      {loading ? (
        <div className="attendance-loading">
          <div className="spinner"></div>
          <p>Fetching class roster...</p>
        </div>
      ) : (
        <div className="attendance-table-card">
          <table className="attendance-table">
            <thead>
              <tr>
                <th style={{ width: "80px" }}>Roll No</th>
                <th>Student Name</th>
                <th style={{ textAlign: "center" }}>Attendance Status</th>
              </tr>
            </thead>
            <tbody>
              {studentList.map((student) => (
                <tr key={student.id} className={`status-row ${student.status.toLowerCase()}`}>
                  <td className="roll-no">{student.rollNo}</td>
                  <td className="student-name">{student.name}</td>
                  <td className="status-options">
                    <div className="segmented-control">
                      <button
                        type="button"
                        className={`option-btn present ${
                          student.status === "Present" ? "active" : ""
                        }`}
                        onClick={() => handleStatusChange(student.id, "Present")}
                      >
                        <FaCheckCircle /> Present
                      </button>

                      <button
                        type="button"
                        className={`option-btn absent ${
                          student.status === "Absent" ? "active" : ""
                        }`}
                        onClick={() => handleStatusChange(student.id, "Absent")}
                      >
                        <FaTimesCircle /> Absent
                      </button>

                      <button
                        type="button"
                        className={`option-btn late ${
                          student.status === "Late" ? "active" : ""
                        }`}
                        onClick={() => handleStatusChange(student.id, "Late")}
                      >
                        <FaClock /> Late
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Form Actions */}
          <div className="table-footer-actions">
            <button
              type="button"
              className="btn-save-attendance"
              onClick={handleSaveAttendance}
              disabled={isSaving}
            >
              <FaSave /> {isSaving ? "Saving..." : "Save Attendance"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceTracker;